import { Box, Typography, Chip, Button, useMediaQuery } from "@mui/material";
import { useTranslation } from 'react-i18next';
import LoadingGif from "../../components/LoadingGif";
import { useState, useEffect, } from "react";
import { useRoom } from "../../context/RoomContext";
import { ChevronLeft, ChevronRight } from "lucide-react";


const TenantsManagementTab = ({
  handleOpenModal,
  bookings,
  setBookings,
  setTodayBookings,
  publishedRooms,
  displayDate,
  displayTime,
  daysRemaining,
}) => {
  const { t } = useTranslation();
  const { ListRoomBookings, loading, } = useRoom();
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const isMobile = useMediaQuery("(max-width:1024px)");

  useEffect(() => {
    const fetchFilteredBookings = async () => {
      const targetRooms = [...new Set(publishedRooms.data.map((room) => room.roomId))];

      const params = { page };
      params.status = "tenants";

      const results = await Promise.all(
        targetRooms.map((room) => ListRoomBookings(room, params))
      );
      const allBookings = results.flatMap((result) => result?.data || result || []);
      setBookings(allBookings || []);
      setTodayBookings(allBookings || []);
      const maxPageArray = results.flatMap((result) => result.pagination.totalPages || []);
      const maxPage = Math.max(...maxPageArray, 1);
      setTotalPages(maxPage);
    };
    fetchFilteredBookings();
  }, [ListRoomBookings, page]);

  const decideStatus = (booking) => {
    const timeZone = "Asia/Taipei";
    const todayStr = new Date().toLocaleDateString("en-US", { timeZone });
    const checkOutStr = new Date(booking.checkOut.scheduledCheckOut).toLocaleDateString("en-US", { timeZone });
    const today = new Date(todayStr);
    const checkOutDate = new Date(checkOutStr);

    if (booking.checkIn.isCheckedIn && checkOutDate.toDateString() === today.toDateString() && !booking.checkOut.isCheckOut) {
      return "CheckOut";
    } else {
      return "inStay";
    }
  };

  return (
    loading ? (
      <LoadingGif />
    ) : (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3, width: "100%", }}>
        {/* Tenants */}
        <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(3, 1fr)", alignItems: "center", justifyContent: "space-between", gap: 2, width: "100%", }}>
          {bookings.map((booking, index) => (
            <Box key={index}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                justifyContent: "center",
                backgroundColor: "#FFF",
                borderRadius: "10px",
                padding: 2,
                boxShadow: "0px 0px 4px rgba(0, 0, 0, 0.25);",
                "&:hover": { boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)", },
              }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", mb: 1, }}>
                <Typography sx={{ fontSize: "14px", backgroundColor: "#F3F4F6", color: "#4A5565", borderRadius: "20px", padding: "8px 12px", fontWeight: 700, }}>
                  {publishedRooms.data.find((room) => room._id === booking.checkIn.assignedRooms)?.roomName || t("None")}
                </Typography>
                <Chip
                  label={t(decideStatus(booking))}
                  size="large"
                  sx={{
                    backgroundColor: decideStatus(booking) === "CheckOut" ? "#E5194A" : "#FF6900",
                    color: "#fff",
                    fontSize: "14px",
                    padding: "8px 12px",
                  }}
                />
              </Box>
              <Typography sx={{ fontSize: "20px", color: "#000", fontWeight: 700, }}>
                {booking.petName}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", mb: 1, }}>
                <Typography sx={{ fontSize: "16px", color: "#000", }}>
                  {booking.name}
                </Typography>
                <Typography sx={{ fontSize: "16px", color: "#000", }}>
                  {booking.phoneNumber}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", }}>
                <Typography sx={{ fontSize: "14px", color: "#4A5565", }}>
                  {t("ScheduledCheckIn")}:
                </Typography>
                <Typography sx={{ fontSize: "14px", color: "#4A5565", }}>
                  {displayDate(booking.checkIn.scheduledCheckIn)} {displayTime(booking.checkIn.scheduledCheckIn)}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", mb: 1 }}>
                <Typography sx={{ fontSize: "14px", color: "#4A5565", }}>
                  {t("ScheduledCheckOut")}:
                </Typography>
                <Typography sx={{ fontSize: "14px", color: "#4A5565", }}>
                  {displayDate(booking.checkOut.scheduledCheckOut)} {displayTime(booking.checkOut.scheduledCheckOut)}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", }}>
                {daysRemaining(booking) === "TodayCheckOuts" ? (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, padding: "6px 0px", }}>
                    <Box
                      component="img"
                      src="/icons/todayCheckOut.svg"
                      alt="Today Check-Out"
                      sx={{ width: "24px", height: "auto" }}
                    />
                    <Typography sx={{ fontSize: "14px", color: "#E5194A", }}>
                  {t("TodayCheckOuts")}
                </Typography> 
                  </Box>
                ) : (
                  <Typography sx={{ fontSize: "14px", backgroundColor: "#F3F4F6", color: "#4A5565", borderRadius: "20px", padding: "8px 12px", fontWeight: 700, }}>
                    {daysRemaining(booking)}
                  </Typography>
                )}
                <Typography sx={{ fontSize: "14px", color: "#31BACD", textDecoration: "underline", cursor: "pointer", }} onClick={() => handleOpenModal(booking._id, decideStatus(booking) === "CheckOut" ? "tenantsCheckOut" : "tenantsInStay")}>
                  {t("View")}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        {/* Pagination Tab */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 2, }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#FFFFFF", borderRadius: "10px", gap: 1, }}>
            <Button
              onClick={() => {
                setPage((prev) => Math.max(1, prev - 1));
              }}
              disabled={page <= 1}
              sx={{ minWidth: "auto", color: "#31BACD", padding: "8px", borderRadius: "10px", "&:disabled": { color: "#D1D1D1" } }}
            >
              <ChevronLeft size={20} />
            </Button>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", }}>
              <Typography sx={{ fontSize: "16px", color: "#000", textAlign: "center", padding: "6px", }}>
                {t("Page")}:
              </Typography>
              <Typography sx={{ fontSize: "16px", color: "#000", textAlign: "center", }}>
                {page}
              </Typography>
              <Typography sx={{ fontSize: "16px", color: "#000", textAlign: "center", padding: "6px", }}>
                /
              </Typography>
              <Typography sx={{ fontSize: "16px", color: "#000", textAlign: "center", }}>
                {totalPages}
              </Typography>
            </Box>
            <Button
              onClick={() => {
                setPage((prev) => Math.min(totalPages, prev + 1));
              }}
              disabled={page >= totalPages}
              sx={{ minWidth: "auto", color: "#31BACD", padding: "8px", borderRadius: "10px", "&:disabled": { color: "#D1D1D1" } }}
            >
              <ChevronRight size={20} />
            </Button>
          </Box>
        </Box>
      </Box>
    )
  );
};

export default TenantsManagementTab;
